export const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'oppref',
];

export const FILLOUT_DEDUPE_STORAGE_KEY = 'padua.fillout.conversions.v1';
export const CONVERSION_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;
const productionHosts = new Set(['paduaweddings.com', 'www.paduaweddings.com']);
const memoryRecords = new Map();

function isUuid(value) {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function recordKey(formId, submissionId) {
  return `${formId}:${submissionId.toLowerCase()}`;
}

function readStoredRecords(storage, now) {
  if (!storage) return {};

  try {
    const stored = JSON.parse(storage.getItem(FILLOUT_DEDUPE_STORAGE_KEY) || '{}');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};

    return Object.fromEntries(
      Object.entries(stored).filter(([, timestamp]) => (
        Number.isFinite(timestamp)
        && now - timestamp >= 0
        && now - timestamp < CONVERSION_RETENTION_MS
      ))
    );
  } catch {
    return {};
  }
}

export function getFilloutStorage(windowObject) {
  try {
    return windowObject?.localStorage;
  } catch {
    return undefined;
  }
}

export function getFilloutAttribution(search = '', captured = {}) {
  const parameters = {};

  if (captured && typeof captured === 'object') {
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = captured[key];
      if (typeof value === 'string' && value.length > 0 && value.length <= 512) {
        parameters[key] = value;
      }
    });
  }

  const query = new URLSearchParams(search);
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = query.get(key);
    if (value && value.length <= 512) parameters[key] = value;
  });

  return parameters;
}

export function getFilloutDirectUrl(formId, parameters = {}) {
  const query = new URLSearchParams();
  ATTRIBUTION_KEYS.forEach((key) => {
    const value = parameters[key];
    if (typeof value === 'string' && value.length > 0 && value.length <= 512) {
      query.set(key, value);
    }
  });

  const queryString = query.toString();
  return `https://form.fillout.com/t/${encodeURIComponent(formId)}${queryString ? `?${queryString}` : ''}`;
}

export function trackFilloutConversion({
  formId,
  submissionId,
  hostname,
  gtag,
  storage,
  now = Date.now(),
}) {
  if (!formId || !isUuid(submissionId) || !productionHosts.has(hostname)) return false;
  if (typeof gtag !== 'function') return false;

  for (const [key, timestamp] of memoryRecords) {
    if (now - timestamp < 0 || now - timestamp >= CONVERSION_RETENTION_MS) {
      memoryRecords.delete(key);
    }
  }

  const key = recordKey(formId, submissionId);
  const storedRecords = readStoredRecords(storage, now);

  if (memoryRecords.has(key) || Object.prototype.hasOwnProperty.call(storedRecords, key)) {
    return false;
  }

  memoryRecords.set(key, now);
  storedRecords[key] = now;

  try {
    storage?.setItem(FILLOUT_DEDUPE_STORAGE_KEY, JSON.stringify(storedRecords));
  } catch {
    // In-memory deduplication still prevents repeated callbacks during this page session.
  }

  gtag('event', 'ads_conversion_Form_1', {
    form_id: formId,
    lead_id: submissionId,
  });

  return true;
}

export function resetFilloutConversionMemoryForTests() {
  memoryRecords.clear();
}